import React, { SyntheticEvent } from 'react';
import './OrgUnitDiagram.css';
import { Box, Checkbox, FormControlLabel, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, entityTypeToLink, generateEmployeePictureUrl } from '@sharedInterfaces/globalEnums';
import type { IOrgUnitDetailDTO } from '@sharedInterfaces/IOrgUnit';
import type { ISmallEmployee } from '@sharedInterfaces/IWhoIAm';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { IOrgUnitTexts } from '@sharedInterfaces/Language/languageTexts/orgUnit';
import type { AppState } from '@store/store';
import Tree, { type RawNodeDatum, type TreeNodeDatum } from 'react-d3-tree';
import { useSelector } from 'react-redux';

interface BasicComponentProps {
    units: IOrgUnitDetailDTO[];
}

function OrgUnitDiagram(props: BasicComponentProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).orgUnit;
    const companyId = useSelector((state: AppState) => state.company.companyId);
    const OUs = props.units;
    const employees = useSelector((state: AppState) => state.company.allEmployees);
    const [translate, setTranslate] = React.useState({ x: 0, y: 0 });
    const treeContainerRef = React.useRef<HTMLDivElement>(null);
    const [height, setHeight] = React.useState(0);
    const [maxNodeWidth, setMaxNodeWidth] = React.useState(50);

    const [showEmployees, setShowEmployees] = React.useState(false);

    const orgChart = generateOrgChart(employees, OUs, langStrings, companyId, showEmployees);

    React.useEffect(() => {
        // Calculate the maximum width of all nodes
        const maxWidth = orgChart ? calculateMaxNodeWidth(orgChart) : 50;
        setMaxNodeWidth(maxWidth);
    }, [orgChart]);

    function calculateMaxNodeWidth(node: RawNodeDatum) {
        const maxWidth = calculateNodeSize(node);
        return maxWidth;
    }

    React.useEffect(() => {
        const handleResize = () => {
            if (!treeContainerRef.current) return;
            const containerWidth = treeContainerRef.current.offsetWidth;
            // const containerHeight = treeContainerRef.current.offsetHeight;

            setTranslate({
                x: containerWidth / 2,
                y: 50,
            });
            setHeight(
                treeContainerRef.current.parentElement?.parentElement
                    ? treeContainerRef.current.parentElement.parentElement.offsetHeight
                    : 0,
            );
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [treeContainerRef, treeContainerRef.current]);
    const theme = useTheme();

    return (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                borderRadius: 3,
                p: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, transparent 50%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                    pointerEvents: 'none',
                },
            }}
        >
            <FormControlLabel
                label={langStrings.showEmployees}
                control={
                    <Checkbox
                        onClick={() => setShowEmployees(!showEmployees)}
                        checked={showEmployees}
                        sx={{ color: theme.palette.primary.main }}
                    />
                }
                sx={{ mb: 2, position: 'relative', zIndex: 1 }}
            />
            <Box
                id="treeWrapper"
                sx={{
                    width: '100%',
                    height: height,
                    position: 'relative',
                    zIndex: 1,
                    '& .node__root, & .node__branch, & .node__leaf': {
                        '& rect': {
                            fill: alpha(theme.palette.background.paper, 0.9),
                            stroke: theme.palette.primary.main,
                            strokeWidth: 2,
                            rx: 12,
                            filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.2)})`,
                        },
                        '& text.title': {
                            fill: theme.palette.text.primary,
                            fontWeight: 400,
                            fontSize: '1rem',
                        },
                        '& text:not(.title):not(.employeeText)': {
                            fill: theme.palette.text.secondary,
                            fontSize: '11px',
                        },
                        '& circle.employee': {
                            fill: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            stroke: theme.palette.primary.main,
                            strokeWidth: 2,
                        },
                        '& text.employeeText': {
                            fill: theme.palette.text.primary,
                            fontSize: '10px',
                            fontWeight: 500,
                        },
                    },
                    '& .rd3t-link': {
                        stroke: theme.palette.primary.main,
                        strokeWidth: 2,
                        opacity: 0.7,
                    },
                }}
                ref={treeContainerRef}
            >
                <Tree
                    data={orgChart}
                    orientation="vertical"
                    pathFunc={'step'}
                    zoom={1}
                    collapsible={false}
                    translate={translate}
                    rootNodeClassName="node__root"
                    branchNodeClassName="node__branch"
                    leafNodeClassName="node__leaf"
                    nodeSize={{ x: maxNodeWidth, y: 100 }}
                    // nodeSize={{ x: 200, y: 200 }}
                    separation={{ siblings: 1, nonSiblings: 1 }}
                    renderCustomNodeElement={renderNodeWithCustomContent}
                    // onNodeClick={(node, evt) =>
                    // {
                    //     console.log(evt)
                    //     console.log(node)
                    // }}
                />
            </Box>
        </Box>
    );
}

export default OrgUnitDiagram;

function generateOrgChart(
    employees: ISmallEmployee[],
    OUs: IOrgUnitDetailDTO[],
    langStrings: IOrgUnitTexts,
    companyId: string,
    showEmployees: boolean,
) {
    let rootOU = OUs.find((o) => o.parentOU === undefined || o.parentOU === null) as IOrgUnitDetailDTO;
    if (!rootOU) {
        if (OUs.length > 0) {
            // console.log("Set fallback ", OUs[0])
            rootOU = OUs[0];
        } else {
            return { name: 'Fehler' } as RawNodeDatum;
        }
    }

    return fillSubOUs(rootOU, OUs, employees, langStrings, companyId, showEmployees);
}

function fillSubOUs(
    ou: IOrgUnitDetailDTO,
    OUs: IOrgUnitDetailDTO[],
    employees: ISmallEmployee[],
    langStrings: IOrgUnitTexts,
    companyId: string,
    showEmployees: boolean,
) {
    const node = {} as RawNodeDatum;
    const ouEmps = employees.filter((se) => ou.employees.find((e) => e.id === se.id));

    node.name = ou.title;
    const subOU = OUs.filter((o) => o.parentOU === ou.id);
    node.children = subOU.map((o) => fillSubOUs(o, OUs, employees, langStrings, companyId, showEmployees));

    if (showEmployees) {
        node.children = node.children.concat(
            ouEmps.map((e) => ({
                name: e.title,
                attributes: {
                    type: 'employee',
                    id: e.id,
                    pictureUrl: e.pictureId ? generateEmployeePictureUrl(companyId, e, 38) : '',
                    initials: extractInitials(e.title),
                },
            })),
        );
    }

    const leader = employees.find((e) => e.id === ou.leader.id);
    node.attributes = {
        type: 'ou',
        id: ou.id,
        Leader: leader ? `${langStrings.leader}: ${leader.title}` : '',
        Employees: ouEmps.length.toString(),
    };
    return node;
}

function extractInitials(title: string): string {
    const names = title.split(' ');
    if (names.length === 1) {
        return names[0].charAt(0); // Nur das erste Zeichen, wenn nur ein Name vorhanden ist
    } else {
        return names.map((name) => name.charAt(0)).join(''); // Die ersten Buchstaben von Vor- und Nachname kombinieren
    }
}

const renderNodeWithCustomContent = ({ nodeDatum }: { nodeDatum: CustomRawNodeDatum }) => {
    const maxTextWidth = calculateNodeSize(nodeDatum);
    const rectHeight = 50;
    const type = nodeDatum.attributes?.type;

    let padding = 0;
    if (type === 'ou') {
        padding = 40; // Mehr Padding für OrgUnit Cards
    }

    const rectWidth = maxTextWidth + padding;
    return type === 'ou' ? renderOrgUnit(nodeDatum, rectWidth, rectHeight) : renderEmployee(nodeDatum);
};

const getTextWidth = (text: string, fontSize: number) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;
    context.font = `${fontSize}px Roboto`;
    return context.measureText(text).width;
};

const calculateNodeSize = (nodeDatum: RawNodeDatum): number => {
    const type = nodeDatum.attributes?.type;
    let nodeWidth = 0;
    if (type === 'ou') {
        nodeWidth = Math.max(
            getTextWidth(nodeDatum.name, 16),
            nodeDatum.attributes?.Leader ? getTextWidth(nodeDatum.attributes.Leader as string, 12) : 0,
        );
    } else {
        nodeWidth = Math.max(getTextWidth(nodeDatum.name, 12));
    }
    const biggestChild = (nodeDatum.children || []).reduce((max, child) => {
        const childWidth = calculateNodeSize(child);
        return childWidth > max ? childWidth : max;
    }, 0);
    return Math.max(nodeWidth, biggestChild);
};

const renderOrgUnit = (nodeDatum: CustomRawNodeDatum, rectWidth: number, rectHeight: number) => {
    const rectX = -rectWidth / 2;
    const rectY = -rectHeight / 2;
    const id = nodeDatum.attributes?.id as string;
    const leader = nodeDatum.attributes?.Leader;

    return (
        <g
            onClick={(e) => {
                const link = entityTypeToLink(id, EEntityType.ORGANISATIONAL_UNIT);
                location.href = link;
            }}
        >
            <rect width={rectWidth} height={rectHeight} x={rectX} y={rectY} />
            <text
                className="title"
                x={0}
                y={rectY + 15 + (leader ? 0 : 10)}
                textAnchor="middle"
                alignmentBaseline="middle"
                style={{ fontSize: '1rem', fontWeight: 400 }}
            >
                {nodeDatum.name}
            </text>
            {leader && (
                <text x={0} y={rectY + 37} textAnchor="middle" alignmentBaseline="middle">
                    {leader}
                </text>
            )}
        </g>
    );
};

interface CustomRawNodeDatum extends TreeNodeDatum {
    id?: string;
}

const renderEmployee = (nodeDatum: CustomRawNodeDatum) => {
    const imageSize = 40;
    const imageX = -imageSize / 2;
    const imageY = -imageSize / 2;
    const imageUrl = nodeDatum.attributes?.pictureUrl as string;
    const initials = nodeDatum.attributes?.initials as string;
    const id = nodeDatum.attributes?.id as string;

    return (
        <g
            onClick={(e) => {
                const link = entityTypeToLink(id, EEntityType.EMPLOYEE);
                location.href = link;
            }}
        >
            {imageUrl !== '' && (
                <>
                    <defs>
                        <clipPath id={`clip-circle-${nodeDatum.id}`}>
                            <circle cx={0} cy={0} r={imageSize / 2} />
                        </clipPath>
                    </defs>
                    <image
                        href={imageUrl}
                        x={imageX}
                        y={imageY}
                        width={imageSize}
                        height={imageSize}
                        clipPath={`url(#clip-circle-${nodeDatum.id})`}
                        preserveAspectRatio="xMidYMid slice"
                    />
                </>
            )}
            {imageUrl === '' && initials !== '' && (
                <>
                    <circle className="employee" cx={0} cy={0} r={imageSize / 2} />
                    <text x={0} y={0} textAnchor="middle" alignmentBaseline="middle">
                        {initials}
                    </text>
                </>
            )}
            <circle cx={0} cy={0} r={imageSize / 2} fill="none" stroke="black" />
            <text x={0} y={30} className="employeeText" textAnchor="middle" alignmentBaseline="middle">
                {nodeDatum.name}
            </text>
        </g>
    );
};
